import api from '../utils/api';
import config from '../config';
import {
  LICENSE_STATUS_APPROVE,
  LICENSE_STATUS_REJECT,
  LICENSE_STATUS_REVOKED,
  LICENSE_STATUS_WAITING,
} from '../constants';

const licenseService = {
  // Get init data (statuses, etc.)
  getInit: async () => {
    return api.get(config.endpoints.licenses.init);
  },

  // Get licenses list with pagination
  getLicenses: async (params = {}) => {
    return api.get(config.endpoints.licenses.list, params);
  },

  // Get license by ID
  getLicenseById: async (id) => {
    return api.get(`${config.endpoints.licenses.getById}/${id}`);
  },

  // Get license history
  getLicenseHistory: async (id, params = {}) => {
    return api.get(`${config.endpoints.licenses.list}/${id}/history`, params);
  },

  // Create new license
  createLicense: async (licenseData) => {
    return api.post(config.endpoints.licenses.create, licenseData);
  },

  // Update license
  updateLicense: async (licenseData) => {
    return api.put(`${config.endpoints.licenses.update}/${licenseData.id}`, licenseData);
  },

  // Delete license
  deleteLicense: async (id) => {
    return api.delete(`${config.endpoints.licenses.delete}/${id}`);
  },

  // Bulk delete licenses
  bulkDeleteLicenses: async (ids) => {
    return api.delete(config.endpoints.licenses.delete, {
      params: { ids },
      paramsSerializer: {
        indexes: null, // Use repeat format: ?ids=1&ids=2 instead of ?ids[0]=1&ids[1]=2
      },
    });
  },

  // Review actions - all use update-status endpoint with different status values
  approveLicense: async (id) => {
    return api.put(`${config.endpoints.licenses.list}/update-status`, { id, status: LICENSE_STATUS_APPROVE });
  },

  rejectLicense: async (id, statusNote) => {
    return api.put(`${config.endpoints.licenses.list}/update-status`, { id, status: LICENSE_STATUS_REJECT, statusNote });
  },

  extendLicense: async (id, newExpiryDate) => {
    // newExpiryDate is a YYYY-MM-DD string, convert to ISO format for backend
    const isoDate = new Date(newExpiryDate).toISOString();
    return api.put(`${config.endpoints.licenses.list}/extend`, { id, newExpiryDate: isoDate });
  },

  revokeLicense: async (id, statusNote) => {
    return api.put(`${config.endpoints.licenses.list}/update-status`, { id, status: LICENSE_STATUS_REVOKED, statusNote });
  },

  // Assign unit to license
  assignUnit: async ({ id, unitId }) => {
    return api.put(`${config.endpoints.licenses.list}/assign-unit`, { id, unitId });
  },

  // Import licenses from Excel file
  importLicenses: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(config.endpoints.licenses.import, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  // Export licenses to Excel
  exportToExcel: async (params = {}) => {
    // Build query string manually to handle arrays properly
    const queryParts = [];

    Object.keys(params).forEach(key => {
      const value = params[key];

      // Skip null, undefined, empty string
      if (value === null || value === undefined || value === '') {
        return;
      }

      // Handle arrays - append each item with same key (Status=1&Status=2)
      if (Array.isArray(value)) {
        if (value.length > 0) {
          value.forEach(item => {
            // Map frontend keys to backend keys (case-sensitive)
            const backendKey = key === 'status' ? 'Status' : key === 'unitId' ? 'UnitId' : key;
            queryParts.push(`${backendKey}=${encodeURIComponent(item)}`);
          });
        }
        // Skip empty arrays
      } else {
        // Handle non-array values
        queryParts.push(`${key}=${encodeURIComponent(value)}`);
      }
    });

    // Build final URL
    const queryString = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
    const url = `${config.api.baseURL}${config.endpoints.licenses.exportExcel}${queryString}`;

    // Download file using fetch
    const token = document.cookie
      .split('; ')
      .find(row => row.startsWith('token='))
      ?.split('=')[1];

    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Export failed');
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `Licenses_${new Date().toISOString().slice(0,10)}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  },

  // Export licenses to PDF
  exportToPdf: async (params = {}) => {
    // Build query string manually to handle arrays properly
    const queryParts = [];

    Object.keys(params).forEach(key => {
      const value = params[key];

      // Skip null, undefined, empty string
      if (value === null || value === undefined || value === '') {
        return;
      }

      // Handle arrays - append each item with same key (Status=1&Status=2)
      if (Array.isArray(value)) {
        if (value.length > 0) {
          value.forEach(item => {
            // Map frontend keys to backend keys (case-sensitive)
            const backendKey = key === 'status' ? 'Status' : key === 'unitId' ? 'UnitId' : key;
            queryParts.push(`${backendKey}=${encodeURIComponent(item)}`);
          });
        }
        // Skip empty arrays
      } else {
        // Handle non-array values
        queryParts.push(`${key}=${encodeURIComponent(value)}`);
      }
    });

    // Build final URL
    const queryString = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
    const url = `${config.api.baseURL}${config.endpoints.licenses.exportPdf}${queryString}`;

    // Download file using fetch
    const token = document.cookie
      .split('; ')
      .find(row => row.startsWith('token='))
      ?.split('=')[1];

    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Export failed');
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `Licenses_${new Date().toISOString().slice(0,10)}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  },

  // Download import template
  downloadTemplate: async () => {
    const url = `${config.api.baseURL}${config.endpoints.licenses.importTemplate}`;

    // Download file using fetch
    const token = document.cookie
      .split('; ')
      .find(row => row.startsWith('token='))
      ?.split('=')[1];

    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Download template failed');
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = 'License_Import_Template.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  },
};

export default licenseService;
